// src/pages/AdminDashboardPage.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";

const font = "Montserrat, sans-serif";

const SVC_COLORS = [
  "#163244","#1b7f4e","#7b5ea7","#b06a00","#185FA5","#c0392b",
  "#0F6E56","#854F0B","#2E4057","#5C6BC0","#00838F","#E65100",
];

const SVC_LABELS = {
  jardinero:"Jardinería", plomeros:"Plomería", electricista:"Electricista",
  gasista:"Gasista", albanileria:"Albañilería", pintores:"Pintores",
  cerrajeria:"Cerrajería", aire_acondicionado:"Aire Acond.", tecnologia:"Tecnología",
  mudanzas:"Mudanzas", carpinteria:"Carpintería", arquitectura:"Arquitectura",
  belleza_personal:"Belleza", control_plagas:"Plagas", cuidado_mayores:"C.Mayores",
  cuidado_ninos:"C.Niños", decoradores:"Decoradores", entrenamiento:"Entrenamiento",
  herrero:"Herrero", instalador_general:"Instalador", mascotas:"Mascotas",
  mecanica_automotriz:"Mecánica", piletas:"Piletas", seguridad:"Seguridad",
  servicio_domestico:"Serv.Dom.", tapicero:"Tapicero",
};

const STATUS_CFG = {
  created:   { label:"Creada",      color:"#163244" },
  assigned:  { label:"Asignada",    color:"#E7A432" },
  completed: { label:"Completada",  color:"#1b7f4e" },
  cancelled: { label:"Cancelada",   color:"#c0392b" },
  reviewing: { label:"En revisión", color:"#7b5ea7" },
  pending:   { label:"Pendiente",   color:"#b06a00" },
};

const DIAS = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];

function formatARS(n) {
  if (!n || isNaN(n)) return "$0";
  const num = Number(n);
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000)    return `$${(num / 1000).toFixed(0)}K`;
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(num);
}

// ─── Mini Sparkline SVG ───────────────────────────────────────────────────────
function Sparkline({ data, color = "#163244", h = 40, filled = true }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => Number(d) || 0);
  const mx = Math.max(...vals, 1);
  const mn = Math.min(...vals, 0);
  const range = mx - mn || 1;
  const W = 120;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * W},${h - ((v - mn) / range) * (h - 4) - 2}`).join(" ");
  const area = `0,${h} ${pts} ${W},${h}`;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
      {filled && <polygon points={area} fill={color + "18"} />}
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ─── Gráfico de barras horizontales ──────────────────────────────────────────
function HBarChart({ data, labelKey, valueKey, color = "#163244", max: forceMax, showPct, multiColor }) {
  const max = forceMax || Math.max(...data.map(d => Number(d[valueKey])), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      {data.map((d, i) => {
        const val = Number(d[valueKey]);
        const pct = Math.round((val / max) * 100);
        const col = multiColor ? (SVC_COLORS[i % SVC_COLORS.length]) : color;
        return (
          <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#163244", maxWidth: "65%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d[labelKey]}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: col }}>{val}{showPct ? "%" : ""}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: "#f0f5f8", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: col, transition: "width 0.6s ease" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Donut chart SVG ─────────────────────────────────────────────────────────
function DonutChart({ segments, size = 120, thickness = 22 }) {
  const r = (size - thickness) / 2;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((s, d) => s + Number(d.value), 0) || 1;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
      {segments.map((seg, i) => {
        const pct = Number(seg.value) / total;
        const dash = pct * circ;
        const gap  = circ - dash;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r}
            fill="none" stroke={seg.color} strokeWidth={thickness}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset * circ}
            strokeLinecap="butt"
          />
        );
        offset += pct;
        return el;
      })}
    </svg>
  );
}

// ─── Line chart de evolución ──────────────────────────────────────────────────
function LineChart({ data, h = 120 }) {
  if (!data || data.length < 2) return <div style={{ height: h, display: "flex", alignItems: "center", justifyContent: "center", color: "#aab8c2", fontSize: 12 }}>Sin datos</div>;
  const W = 600;
  const pad = { t: 10, r: 10, b: 24, l: 28 };
  const iW = W - pad.l - pad.r;
  const iH = h - pad.t - pad.b;

  const creadas     = data.map(d => Number(d.creadas) || 0);
  const completadas = data.map(d => Number(d.completadas) || 0);
  const maxVal = Math.max(...creadas, ...completadas, 1);

  const toX = i => pad.l + (i / (data.length - 1)) * iW;
  const toY = v => pad.t + iH - (v / maxVal) * iH;

  const line = (vals, color) => {
    const pts = vals.map((v, i) => `${toX(i)},${toY(v)}`).join(" ");
    const area = `${pad.l},${pad.t + iH} ${pts} ${toX(vals.length - 1)},${pad.t + iH}`;
    return (
      <>
        <polygon points={area} fill={color + "12"} />
        <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      </>
    );
  };

  const xLabels = data.filter((_, i) => i % 5 === 0 || i === data.length - 1);
  const ySteps  = [0, Math.round(maxVal / 2), maxVal];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${h}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
      {ySteps.map(v => (
        <g key={v}>
          <line x1={pad.l} y1={toY(v)} x2={W - pad.r} y2={toY(v)} stroke="#f0f5f8" strokeWidth="1" />
          <text x={pad.l - 4} y={toY(v) + 4} textAnchor="end" fontSize="9" fill="#aab8c2">{v}</text>
        </g>
      ))}
      {line(creadas,     "#163244")}
      {line(completadas, "#1b7f4e")}
      {data.map((d, i) => i % 5 === 0 || i === data.length - 1 ? (
        <text key={i} x={toX(i)} y={h - 4} textAnchor="middle" fontSize="9" fill="#aab8c2">
          {d.fecha?.slice(5)}
        </text>
      ) : null)}
    </svg>
  );
}

// ─── Columnas vertical bar ────────────────────────────────────────────────────
function VBarChart({ data, labelKey, valueKey, color = "#163244", h = 80 }) {
  const vals = data.map(d => Number(d[valueKey]));
  const max = Math.max(...vals, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: h }}>
      {data.map((d, i) => {
        const pct = vals[i] / max;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, height: "100%", justifyContent: "flex-end" }}>
            <div style={{ fontSize: 8, color: "#aab8c2", fontWeight: 600 }}>{vals[i] || ""}</div>
            <div style={{ width: "100%", borderRadius: "3px 3px 0 0", background: color, height: `${Math.max(pct * (h - 20), 2)}px`, transition: "height 0.5s ease" }} />
            <div style={{ fontSize: 9, color: "#7a93a4", fontWeight: 600, textAlign: "center" }}>{d[labelKey]}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPI({ icon, label, value, sub, color = "#163244", spark, trend }) {
  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 4, overflow: "hidden", position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</span>
        <span style={{ fontSize: 18 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 900, color, lineHeight: 1.1 }}>
        {value ?? <span style={{ opacity: 0.3, fontSize: 18 }}>—</span>}
      </div>
      {sub && <div style={{ fontSize: 10, color: "#aab8c2" }}>{sub}</div>}
      {trend !== undefined && (
        <div style={{ fontSize: 10, fontWeight: 700, color: trend >= 0 ? "#1b7f4e" : "#c0392b" }}>
          {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)} vs sem. anterior
        </div>
      )}
      {spark && (
        <div style={{ marginTop: 4, opacity: 0.7 }}>
          <Sparkline data={spark} color={color} h={32} />
        </div>
      )}
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, icon, children, action }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 3, height: 14, background: "#E7A432", borderRadius: 2 }} />
          <span style={{ fontWeight: 700, fontSize: 13, color: "#163244" }}>{icon} {title}</span>
        </div>
        {action}
      </div>
      <div style={{ padding: "16px 20px" }}>{children}</div>
    </div>
  );
}

// ─── Pulse indicator ──────────────────────────────────────────────────────────
const REFRESH_OPTIONS = [
  { label: "15s", value: 15 },
  { label: "30s", value: 30 },
  { label: "1min", value: 60 },
  { label: "5min", value: 300 },
];

function Pulse({ countdown, onRefresh, refreshInterval, onChangeInterval }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <div style={{ display: "flex", gap: 3, background: "#f0f5f8", borderRadius: 8, padding: 3 }}>
        {REFRESH_OPTIONS.map(o => (
          <button key={o.value} onClick={() => onChangeInterval(o.value)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: refreshInterval === o.value ? "#163244" : "transparent", color: refreshInterval === o.value ? "#fff" : "#5a6e7f", fontFamily: font, fontWeight: 700, fontSize: 10, cursor: "pointer", transition: "all 0.15s" }}>
            {o.label}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1b7f4e", animation: "pulse 1.5s infinite" }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: "#1b7f4e" }}>En vivo</span>
      </div>
      <span style={{ fontSize: 11, color: "#aab8c2" }}>en <strong style={{ color: "#163244" }}>{countdown}s</strong></span>
      <button onClick={onRefresh} style={{ padding: "6px 14px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.2)", background: "#fff", color: "#163244", fontFamily: font, fontWeight: 700, fontSize: 11, cursor: "pointer" }}>↻ Ahora</button>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminDashboardPage() {
  const navigate = useNavigate();

  const [stats,         setStats]         = useState(null);
  const [finStats,      setFinStats]      = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [lastUpdate,    setLastUpdate]    = useState(null);
  const [countdown,     setCountdown]     = useState(30);
  const [rankTab,       setRankTab]       = useState("best");
  const [refreshSec,    setRefreshSec]    = useState(30);
  const [provinciaFilt, setProvinciaFilt] = useState("todas");
  const [allProvincias, setAllProvincias] = useState([]);
  const iRef = useRef(null);
  const cRef = useRef(null);

  useEffect(() => {
    api.get("/admin/stats")
      .then(res => {
        const lista = (res.data.por_provincia || [])
          .filter(p => p.provincia && p.provincia !== "Sin especificar")
          .map(p => p.provincia);
        setAllProvincias(lista);
      })
      .catch(() => {});
  }, []);

  const fetchStats = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (provinciaFilt && provinciaFilt !== "todas") params.provincia = provinciaFilt;
      const [statsRes, finRes] = await Promise.all([
        api.get("/admin/stats", { params }),
        api.get("/payments/stats").catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setFinStats(finRes.data);
      setLastUpdate(new Date());
      setCountdown(refreshSec);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Error al cargar estadísticas.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [provinciaFilt, refreshSec]);

  useEffect(() => {
    fetchStats();
    clearInterval(iRef.current);
    clearInterval(cRef.current);
    iRef.current = setInterval(() => fetchStats(true), refreshSec * 1000);
    cRef.current = setInterval(() => setCountdown(c => c > 0 ? c - 1 : refreshSec), 1000);
    setCountdown(refreshSec);
    return () => { clearInterval(iRef.current); clearInterval(cRef.current); };
  }, [fetchStats, refreshSec]);

  if (loading) return (
    <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: font, gap: 12 }}>
      <span style={{ width: 32, height: 32, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
      <div style={{ fontSize: 13, color: "#7a93a4", fontWeight: 600 }}>Cargando estadísticas…</div>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%{box-shadow:0 0 0 0 rgba(27,127,78,0.4)}70%{box-shadow:0 0 0 8px rgba(27,127,78,0)}100%{box-shadow:0 0 0 0 rgba(27,127,78,0)}}"}</style>
    </div>
  );

  if (error && !stats) return (
    <div style={{ padding: 40, textAlign: "center", fontFamily: font }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
      <div style={{ color: "#c0392b", fontWeight: 700, marginBottom: 16 }}>{error}</div>
      <button onClick={() => fetchStats()} style={{ padding: "10px 24px", borderRadius: 50, background: "#163244", color: "#fff", border: "none", fontFamily: font, fontWeight: 700, cursor: "pointer" }}>Reintentar</button>
    </div>
  );

  const u = stats?.usuarios    || {};
  const p = stats?.prestadores || {};
  const r = stats?.solicitudes || {};
  const ev = stats?.evolucion_diaria || [];

  const sparkCreadas     = ev.map(d => Number(d.creadas));
  const sparkCompletadas = ev.map(d => Number(d.completadas));

  const donutSegments = (stats?.por_estado || []).map(s => ({
    label: STATUS_CFG[s.status]?.label || s.status,
    value: Number(s.count),
    color: STATUS_CFG[s.status]?.color || "#aab8c2",
  }));
  const donutTotal = donutSegments.reduce((a, s) => a + s.value, 0);

  const weekData = Array.from({ length: 7 }, (_, i) => {
    const found = (stats?.por_dia_semana || []).find(d => Number(d.dia_semana) === i);
    return { dia: DIAS[i], total: found ? Number(found.total) : 0 };
  });

  const tipoUrgente = stats?.por_tipo?.find(t => t.tipo === "urgent");
  const tipoQuote   = stats?.por_tipo?.find(t => t.tipo === "quote");
  const pctUrgente  = tipoUrgente && donutTotal ? Math.round((Number(tipoUrgente.total) / donutTotal) * 100) : 0;

  const provinciasDisp = ["todas", ...allProvincias];
  const isFiltered = provinciaFilt !== "todas";
  const provincias = (stats?.por_provincia || []).filter(p => p.provincia !== "Sin especificar").slice(0, 10);

  const ranking   = stats?.ranking_prestadores || [];
  const top    = [...ranking].sort((a, b) => (Number(b.rating_avg) || 0) - (Number(a.rating_avg) || 0)).slice(0, 8);
  const worst  = [...ranking].filter(p => p.jobs_completed > 0).sort((a, b) => (Number(a.rating_avg) || 0) - (Number(b.rating_avg) || 0)).slice(0, 8);
  const displayed = rankTab === "best" ? top : worst;

  const fin = finStats || {};
  const hasFinData = fin.completed_count > 0;

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "24px 24px 60px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>

          {/* ── HEADER ─────────────────────────────────────────────────── */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ margin: "0 0 3px", fontSize: 22, fontWeight: 800, color: "#163244" }}>Dashboard</h1>
              <div style={{ fontSize: 12, color: "#7a93a4" }}>
                {lastUpdate ? `Actualizado: ${lastUpdate.toLocaleTimeString("es-AR")}` : "Cargando…"}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#7a93a4" }}>🗺️ Vista:</span>
                <select
                  value={provinciaFilt}
                  onChange={e => setProvinciaFilt(e.target.value)}
                  style={{ padding: "6px 12px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.2)", fontFamily: font, fontSize: 11, fontWeight: 700, background: isFiltered ? "#163244" : "#fff", color: isFiltered ? "#fff" : "#163244", outline: "none", cursor: "pointer" }}
                >
                  {provinciasDisp.map(p => (
                    <option key={p} value={p}>{p === "todas" ? "Todo el país" : p}</option>
                  ))}
                </select>
                {isFiltered && (
                  <button onClick={() => setProvinciaFilt("todas")} style={{ fontSize: 11, color: "#c0392b", background: "none", border: "none", cursor: "pointer", fontFamily: font, fontWeight: 600 }}>✕ Limpiar</button>
                )}
              </div>
              <Pulse
                countdown={countdown}
                onRefresh={() => fetchStats()}
                refreshInterval={refreshSec}
                onChangeInterval={setRefreshSec}
              />
            </div>
          </div>

          {isFiltered && (
            <div style={{ background: "#163244", borderRadius: 10, padding: "10px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
              <span style={{ fontSize: 16 }}>🗺️</span>
              <span style={{ color: "#fff", fontWeight: 600 }}>Mostrando datos de: <strong style={{ color: "#E7A432" }}>{provinciaFilt}</strong></span>
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>— Los KPIs generales siempre muestran el total nacional</span>
              <button onClick={() => setProvinciaFilt("todas")} style={{ marginLeft: "auto", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 20, padding: "4px 12px", color: "#fff", fontFamily: font, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>Ver todo el país</button>
            </div>
          )}

          {/* ── BLOQUE 1: KPIs principales ─────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
            <KPI icon="👥" label="Usuarios totales"    value={u.total}       color="#163244" sub={`+${u.nuevos_semana||0} esta semana`} spark={sparkCreadas} />
            <KPI icon="🔧" label="Prestadores activos" value={p.activos}     color="#1b7f4e" sub={`de ${p.total||0} registrados`} />
            <KPI icon="📋" label="Solicitudes totales" value={r.total}       color="#163244" sub={`${r.hoy||0} hoy · ${r.semana||0} esta semana`} spark={sparkCreadas} />
            <KPI icon="✅" label="Completadas"         value={r.completadas} color="#1b7f4e" sub={`${r.sin_prestador||0} sin prestador asignado`} spark={sparkCompletadas} />
          </div>

          {/* ── BLOQUE 2: KPIs secundarios ─────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 20 }}>
            {[
              { icon:"📥", label:"Creadas",      val: r.creadas,      col:"#163244" },
              { icon:"🤝", label:"Asignadas",    val: r.asignadas,    col:"#E7A432" },
              { icon:"✕",  label:"Canceladas",   val: r.canceladas,   col:"#c0392b" },
              { icon:"🚨", label:"Urgentes",     val: r.urgentes,     col:"#b06a00" },
              { icon:"⏳", label:"Sin prestador", val: r.sin_prestador, col:"#c0392b" },
              { icon:"⭐", label:"Rating prom.", val: p.rating_promedio ? `${parseFloat(p.rating_promedio).toFixed(2)}` : "—", col:"#E7A432" },
            ].map(k => (
              <div key={k.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.07)", padding: "12px 14px" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>{k.icon} {k.label}</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: k.col }}>{k.val ?? "—"}</div>
              </div>
            ))}
          </div>

          {/* ── BLOQUE 3: Evolución + Estado ───────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 16 }}>
            <Section title="Evolución de solicitudes — últimos 30 días" icon="📈">
              <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 10, height: 3, borderRadius: 2, background: "#163244" }} />
                  <span style={{ fontSize: 11, color: "#163244", fontWeight: 600 }}>Creadas</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 10, height: 3, borderRadius: 2, background: "#1b7f4e" }} />
                  <span style={{ fontSize: 11, color: "#1b7f4e", fontWeight: 600 }}>Completadas</span>
                </div>
              </div>
              <LineChart data={ev} h={140} />
            </Section>

            <Section title="Estado actual" icon="🔵">
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <DonutChart segments={donutSegments.filter(s => s.value > 0)} size={110} thickness={20} />
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: "#163244", lineHeight: 1 }}>{donutTotal}</div>
                    <div style={{ fontSize: 8, color: "#aab8c2", fontWeight: 600 }}>TOTAL</div>
                  </div>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  {donutSegments.filter(s => s.value > 0).map(s => (
                    <div key={s.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                        <span style={{ fontSize: 11, color: "#163244", fontWeight: 500 }}>{s.label}</span>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: s.color }}>{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Section>
          </div>

          {/* ── BLOQUE 4: Servicios + Provincias ───────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Section title={`Servicios más solicitados${isFiltered ? ` — ${provinciaFilt}` : ""}`} icon="🛠️">
              <HBarChart
                data={(stats?.por_servicio || []).map(s => ({ label: SVC_LABELS[s.service_key] || s.service_key, total: Number(s.total), completadas: Number(s.completadas) }))}
                labelKey="label" valueKey="total" multiColor
              />
            </Section>

            <Section title="Solicitudes por provincia" icon="🗺️">
              {provincias.length > 0 ? (
                <HBarChart data={provincias} labelKey="provincia" valueKey="total" color="#E7A432" />
              ) : (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#aab8c2", fontSize: 13 }}>Sin datos de provincia disponibles</div>
              )}
            </Section>
          </div>

          {/* ── BLOQUE 5: Demanda semanal + Ciudades + Tipo ────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Section title="Demanda por día" icon="📅">
              <VBarChart data={weekData} labelKey="dia" valueKey="total" color="#163244" h={100} />
            </Section>

            <Section title={`Top ciudades${isFiltered ? ` — ${provinciaFilt}` : ""}`} icon="🏙️">
              <HBarChart
                data={(stats?.ciudades_demanda || []).map((c, i) => ({ label: `${i+1}. ${c.ciudad}`, total: Number(c.total) }))}
                labelKey="label" valueKey="total" color="#7b5ea7"
              />
            </Section>

            <Section title="Tipos y prestadores" icon="📊">
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Tipo de solicitud</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {[
                    { label: "Urgente",    value: tipoUrgente?.total || 0, pct: pctUrgente,        color: "#c0392b" },
                    { label: "Cotización", value: tipoQuote?.total   || 0, pct: 100 - pctUrgente,  color: "#163244" },
                  ].map(t => (
                    <div key={t.label} style={{ flex: 1, background: "#f5f7fa", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 18, fontWeight: 900, color: t.color }}>{t.pct}%</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#7a93a4", marginTop: 2 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: "#aab8c2" }}>{t.value} solicitudes</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)", paddingTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Estado de prestadores</div>
                {[
                  { label: "Activos",      val: p.activos,     color: "#1b7f4e" },
                  { label: "En revisión",  val: p.en_revision, color: "#b06a00" },
                  { label: "Borrador",     val: p.borrador,    color: "#7a93a4" },
                  { label: "Suspendidos",  val: p.suspendidos, color: "#c0392b" },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(0,0,0,0.04)", fontSize: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <div style={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
                      <span style={{ color: "#163244" }}>{label}</span>
                    </div>
                    <span style={{ fontWeight: 700, color }}>{val || 0}</span>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {/* ── BLOQUE 6: Módulo financiero — ACTIVO ───────────────────── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ background: "#163244", borderRadius: 16, padding: "20px 24px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 90% 50%, rgba(231,164,50,0.15) 0%, transparent 55%)", pointerEvents: "none" }} />
              <div style={{ position: "relative", zIndex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#E7A432", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>💰 Módulo financiero</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>
                      {hasFinData ? "Resumen de ingresos" : "Sin transacciones aún"}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/admin/finanzas")}
                    style={{ padding: "8px 20px", borderRadius: 50, border: "none", background: "#E7A432", color: "#163244", fontFamily: font, fontWeight: 800, fontSize: 12, cursor: "pointer" }}
                  >
                    Ver detalle completo →
                  </button>
                </div>

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {[
                    { label: "Volumen total",          value: formatARS(fin.total_volume),    icon: "💳" },
                    { label: "Ingresos FIXO",          value: formatARS(fin.fixo_income),     icon: "📈" },
                    { label: "Liquidaciones pendientes", value: formatARS(fin.pending_payouts), icon: "⏳" },
                    { label: "Pagos hoy",              value: formatARS(fin.today_volume),    icon: "📅" },
                  ].map(({ label, value, icon }) => (
                    <div key={label} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px 18px", textAlign: "center", flex: 1, minWidth: 120 }}>
                      <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: hasFinData ? "#E7A432" : "rgba(255,255,255,0.2)", marginBottom: 2 }}>
                        {hasFinData ? value : "—"}
                      </div>
                      <div style={{ fontSize: 9, fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                    </div>
                  ))}
                </div>

                {hasFinData && (
                  <div style={{ marginTop: 14, display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                      {fin.completed_count} pago{fin.completed_count !== 1 ? "s" : ""} completado{fin.completed_count !== 1 ? "s" : ""}
                    </span>
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                      {fin.pending_payouts_count} liquidación{fin.pending_payouts_count !== 1 ? "es" : ""} pendiente{fin.pending_payouts_count !== 1 ? "s" : ""}
                    </span>
                    {fin.cancelled_count > 0 && (
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                        {fin.cancelled_count} cancelado{fin.cancelled_count !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── BLOQUE 7: Ranking prestadores ──────────────────────────── */}
          <Section
            title="Ranking de prestadores"
            icon="🏆"
            action={
              <div style={{ display: "flex", gap: 4, background: "#f5f7fa", borderRadius: 8, padding: 3 }}>
                {[{ id:"best", label:"Mejores" }, { id:"worst", label:"Para mejorar" }].map(t => (
                  <button key={t.id} onClick={() => setRankTab(t.id)} style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: rankTab === t.id ? "#163244" : "transparent", color: rankTab === t.id ? "#fff" : "#5a6e7f", fontFamily: font, fontWeight: 700, fontSize: 11, cursor: "pointer", transition: "all 0.15s" }}>
                    {t.label}
                  </button>
                ))}
              </div>
            }
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {displayed.map((prov, i) => (
                <div key={prov.id} style={{ background: "#f5f7fa", borderRadius: 12, padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: rankTab === "best" ? "rgba(231,164,50,0.12)" : "rgba(192,57,43,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: rankTab === "best" ? "#b8820d" : "#c0392b", flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#163244", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{prov.display_name}</div>
                    <div style={{ fontSize: 10, color: "#7a93a4", marginBottom: 4 }}>{prov.email}</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: rankTab === "best" ? "#1b7f4e" : "#c0392b" }}>⭐ {prov.rating_avg ? parseFloat(prov.rating_avg).toFixed(1) : "—"}</span>
                      <span style={{ fontSize: 10, color: "#aab8c2" }}>{prov.jobs_completed} trabajos</span>
                    </div>
                  </div>
                </div>
              ))}
              {displayed.length === 0 && (
                <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "20px", color: "#aab8c2", fontSize: 13 }}>Sin datos suficientes para el ranking</div>
              )}
            </div>
          </Section>

        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%{box-shadow:0 0 0 0 rgba(27,127,78,0.4)} 70%{box-shadow:0 0 0 8px rgba(27,127,78,0)} 100%{box-shadow:0 0 0 0 rgba(27,127,78,0)} }
      `}</style>
    </>
  );
}
